// Undocked detail window for the Generic CRUD engine (Phase 5 - see generic_crud.tsx and
// CRUD_engine_test_plan.md "Phase 5 - Undock & Sync"). Opened via window.open from generic_crud.tsx's
// "Undock" button; renders the exact same ChildDetailPanel component the docked page uses
// (REQ-DU-013/014), kept in sync purely by subscribing to the shared BroadcastChannel - no API
// polling, no new backend endpoints.
//
// Known scope limit for this first slice (documented, not silently dropped): sync is one-directional
// (main window -> this window). Changing tabs here, or triggering a custom row action (create-child/
// tag) here, does not propagate back to the main window - use the main window for those. This
// matches the vn04 doc's own non-goal of a full bidirectional workspace manager for the first version.
import { useContext, useEffect, useState } from "react";
import { Box, Text } from "@chakra-ui/react";
import { useApi } from "../hooks/useApi";
import BaseTemplate from "../components/BaseTemplate";
import InternalUserRoute from "../util/internal_user_route";
import { NotificationContext } from "../components/NotificationPane";
import ChildDetailPanel from "../components/GenericCrud/ChildDetailPanel";
import type { Row } from "../components/GenericCrud/shared";
import type { MetadataGraph } from "../schemas/crudMeta";
import type { NotificationContextType } from "../types";
import { requestWorkspaceContext, subscribeWorkspaceContext, type WorkspaceContext } from "../lib/workspaceChannel";

export default function GenericCrudDetailWindow() {
  const { getGenericCrudMeta } = useApi();
  const { pushNotification } = useContext<NotificationContextType | null>(NotificationContext)!;
  const [metaGraph, setMetaGraph] = useState<MetadataGraph | null>(null);
  const [ctx, setCtx] = useState<WorkspaceContext | null>(null);
  // Own copy of the active tab index, seeded from ctx but changeable locally (clicking a tab in
  // this window must actually switch tabs here - see file header re: one-directional sync).
  const [localActiveTab, setLocalActiveTab] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        setMetaGraph(await getGenericCrudMeta());
      } catch (e: any) {
        pushNotification(e?.message || "Failed to load Generic CRUD metadata", "error");
      }
    })();
  }, [getGenericCrudMeta, pushNotification]);

  useEffect(() => {
    const unsubscribe = subscribeWorkspaceContext((next) => {
      setCtx(next);
      setLocalActiveTab(next.activeChildTab);
    });
    // Subscribe first, then ask - otherwise a reply that arrives before this line would run
    // would be missed the same way the opener's own immediate post-open publish can be.
    requestWorkspaceContext();
    return unsubscribe;
  }, []);

  const selectedEntityMeta = ctx?.selectedEntity && metaGraph ? metaGraph.entities[ctx.selectedEntity] : null;
  const activeChild = selectedEntityMeta?.children[localActiveTab];
  const activeChildMeta = activeChild && metaGraph ? metaGraph.entities[activeChild.childEntity] : null;

  const focusedRow: Row | null =
    ctx && selectedEntityMeta && ctx.focusedRowPk != null
      ? { [selectedEntityMeta.pk]: ctx.focusedRowPk, [selectedEntityMeta.displayField]: ctx.focusedRowLabel }
      : null;

  return (
    <InternalUserRoute>
      <BaseTemplate fitToWindow>
        <BaseTemplate.Header>
          <Text fontWeight="semibold">
            Detail{selectedEntityMeta ? ` — ${selectedEntityMeta.label}` : ""}
            {focusedRow && selectedEntityMeta ? `: ${String(focusedRow[selectedEntityMeta.displayField])}` : ""}
          </Text>
        </BaseTemplate.Header>
        <BaseTemplate.Body>
          {!ctx && (
            <Text color="gray.500">Waiting for the main Generic CRUD window to select an entity…</Text>
          )}
          {ctx && !selectedEntityMeta && (
            <Text color="gray.500">Loading…</Text>
          )}
          {selectedEntityMeta && selectedEntityMeta.children.length === 0 && (
            <Text color="gray.500">{selectedEntityMeta.label} has no configured child relationships to show here.</Text>
          )}
          {selectedEntityMeta && selectedEntityMeta.children.length > 0 && (
            <Box w="100%" h="100%" overflow="auto">
              <ChildDetailPanel
                selectedEntity={ctx!.selectedEntity!}
                selectedEntityMeta={selectedEntityMeta}
                focusedRow={focusedRow}
                activeChildTab={localActiveTab}
                onActiveChildTabChange={setLocalActiveTab}
                onRunAction={() => pushNotification("Row actions aren't available in the undocked window yet — use the main window.", "info")}
                activeChildMeta={activeChildMeta}
              />
            </Box>
          )}
        </BaseTemplate.Body>
      </BaseTemplate>
    </InternalUserRoute>
  );
}
